import React, { FC, useCallback, useEffect, useState } from 'react';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

import endpoints from 'consts/endpoints';
import { getFileStorage } from 'components/forms/CompanyForm/reportsData';
import useContracts, { contractDataBaseType } from 'hooks/api/useContracts';

import s from './styles.module.scss';
import { useUserContext } from 'contexts/UserProvider';
import { isAdmin } from 'helpers/token';
import Flex from 'components/Flex';
import { AddIcon, Button, DownloadIcon, EditIcon, Pagination, Select, Table, Tooltip } from '@gamesb42/ui-kit';
import moment from 'moment';
import baseRequest from 'queries/baseRequest';
import { getDocumentTypes } from 'api/docs';
import { getCompanyGeneralDocs } from 'api/company';
import AddDocModal from './AddDocModal';
import EditDocModal from './EditDocModal';
import ActionButton from './ActionButton';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import TextWithTooltip from './TextWithTooltip';
// import Tooltip from 'antd/es/tooltip';

type PropsT = {
  companyId?: string;
  setCurrentDocument: (data: contractDataBaseType) => void;
  setIsOpen: (data: boolean) => void;
};

interface GeneralResI {
  items: any[];
  total: number;
  page: number;
  size: number;
}

export interface QueryParams {
  size?: number;
  page?: number;
  isArchive?: string;
}

const columns = [
  {
    title: 'Type',
    align: 'left' as 'left',
    width: 150,
    dataIndex: 'document_type',
    render: (document_type) => {
      return (
        <TextWithTooltip
          text={document_type}
          maxWidth="150px"
          className={s.text}
          isOverflowing={document_type?.length > 16}
        />
      );
    },
  },
  {
    title: 'No.',
    align: 'left' as 'left',
    width: 102,
    dataIndex: 'document_number',
    render: (document_number) => {
      return (
        <TextWithTooltip
          text={document_number}
          maxWidth="102px"
          className={s.link}
          isOverflowing={document_number?.length >= 8}
        />
      );
    },
  },
  {
    title: 'Name',
    align: 'left' as 'left',
    width: 190,
    dataIndex: 'document_name',
    render: (document_name) => {
      return (
        <TextWithTooltip
          text={document_name}
          maxWidth="190px"
          className={s.text}
          isOverflowing={document_name?.length >= 18}
        />
      );
    },
  },
  {
    title: 'Linked document',
    align: 'left' as 'left',
    width: 190,
    dataIndex: 'parent_document',
    render: (parent_document) => {
      return (
        <>
          {parent_document?.document_name ? (
            <TextWithTooltip
              text={parent_document.document_name}
              maxWidth="190px"
              className={s.link}
              isOverflowing={parent_document.document_name.length >= 18}
              onClick={(e) => {
                e.stopPropagation();
                if (parent_document?.file_id) getFileStorage(endpoints.getStorageFileUrl(parent_document.file_id));
              }}
            />
          ) : (
            <div>-</div>
          )}
        </>
      );
    },
  },
  {
    title: 'Product',
    align: 'left' as 'left',
    width: 110,
    dataIndex: 'product_type',
    render: (product_type) => {
      return (
        <>
          {product_type ? (
            <Flex align="center" justify="center" className={s.product}>
              {product_type.toUpperCase()}
            </Flex>
          ) : (
            <div>-</div>
          )}
        </>
      );
    },
  },
  {
    title: 'Document date',
    align: 'left' as 'left',
    width: 108,
    render: ({ document_date }) => {
      return <div style={{ whiteSpace: 'nowrap' }}>{moment(document_date).format('DD.MM.YYYY')}</div>;
    },
  },
  {
    align: 'center' as const,
    width: '44px',
    noSkeleton: true,
    render: ({ file_id }) => (
      <Button
        className={s.edit}
        icon={<DownloadIcon size={24} />}
        onClick={(e) => {
          e.stopPropagation();
          getFileStorage(endpoints.getStorageFileUrl(file_id));
        }}
        type="iconBtn"
      />
    ),
  },
];

const Agreements: FC<PropsT> = ({ companyId, setCurrentDocument, setIsOpen }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { user } = useUserContext();
  // Убираем локальную переменную isAdmin
  const [docs, setDocs] = useState<GeneralResI>();
  const [docTypes, setDocTypes] = useState([]);
  const [filter, setFilter] = useState({ docType: '', product: '' });
  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [activeRecord, setActiveRecord] = useState();

  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  let page = searchParams.get('page') || 1;
  const size = searchParams.get('size') || 10;

  console.log({ size });

  const isArchive = searchParams.get('isArchive');
  const [isChecked, setIsChecked] = useState(isArchive === 'true');

  const toParams = (filter) => {
    let outString = '';

    if (filter.docType) {
      outString = `?document_type=${filter.docType}`;
    }

    if (filter.product) {
      outString += outString.length > 0 ? `&product=${filter.product}` : `?product=${filter.product}`;
    }

    if (isArchive) {
      outString += outString.length > 0 ? `&show_deleted=${isArchive}` : `?show_deleted=${isArchive}`;
    }

    outString +=
      outString.length > 0 ? `&page=${Number(page) - 1}&limit=${size}` : `?page=${Number(page) - 1}&limit=${size}`;

    outString += '&only_custom=true';

    return outString;
  };

  const onChangeQuery = useCallback(
    (newValues: Partial<QueryParams>) => {
      history.replace(
        `${history.location.pathname}?${queryString.stringify(
          { size, page, ...newValues },
          { skipEmptyString: true },
        )}`,
      );
    },
    [history, size, page],
  );

  function getList() {
    getCompanyGeneralDocs(companyId, toParams(filter))
      .then((res) => setDocs(res))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    getList();
  }, [filter, page, size, isArchive]);

  useEffect(() => {
    getDocumentTypes().then((result) => {
      setDocTypes(result.map(({ id, title }) => ({ label: title, value: id })));
    });
  }, []);

  return (
    <>
      <Flex className={s.wrapper} direction="column">
        <Flex style={{ marginBottom: '12px' }}>
          <Flex gap="12px">
            <div style={{ width: '260px' }}>
              <Select
                label={'Document type'}
                options={docTypes}
                size="middle"
                value={filter.docType}
                onChange={(e) => setFilter((prevVal) => ({ ...prevVal, docType: e }))}
                allowClear
              />
            </div>
            <div style={{ width: '200px' }}>
              <Select
                label={'Product'}
                options={[
                  { label: 'Now', value: 'now' },
                  { label: 'Future', value: 'future' },
                  { label: 'Max', value: 'max' },
                ]}
                size="middle"
                value={filter.product}
                onChange={(e) => setFilter((prevVal) => ({ ...prevVal, product: e }))}
                allowClear
              />
            </div>
          </Flex>

          <Flex gap="24px" justify="flex-end">
            <Button
              style={{ backgroundColor: '#9197AD', color: '#fff', border: 'none' }}
              icon={<AddIcon size={16} />}
              onClick={() => setIsOpenAddModal(true)}
            >
              Upload document
            </Button>
            <ActionButton isChecked={isChecked} setIsChecked={setIsChecked} />
          </Flex>
        </Flex>

        <Flex direction="column" gap="20px">
          <Table
            loading={loading}
            columns={columns}
            dataSource={docs?.items}
            className={s.table}
            rowClassName={classNames(s.row, isArchive === 'true' && s.deleted)}
            initialHeight="600px"
            onRow={(record) => ({
              onClick: () => {
                setActiveRecord(record);
                setIsOpenEditModal(true);
              },
            })}
            components={{
              body: {
                row: (props) => {
                  const rowProps = props.children[0]?.props;
                  const hasComment = rowProps?.record?.comment;
                  return hasComment ? (
                    <Tooltip title={rowProps?.record?.comment}>
                      <tr {...props} />
                    </Tooltip>
                  ) : (
                    <tr {...props} />
                  );
                },
              },
            }}
          />
          <Pagination
            total={docs?.total}
            current={Number(page)}
            pageSize={Number(size)}
            onChange={(newCurrent: number, newPageSize: number) =>
              onChangeQuery({ page: newCurrent, size: newPageSize })
            }
          />
        </Flex>
      </Flex>
      {isOpenAddModal && (
        <AddDocModal
          onClose={() => {
            setIsOpenAddModal(false);
          }}
          getData={getList}
          docTypes={docTypes}
        />
      )}
      {isOpenEditModal && (
        <EditDocModal
          onClose={() => {
            setIsOpenEditModal(false);
          }}
          getData={getList}
          docTypes={docTypes}
          activeRecord={activeRecord}
        />
      )}
    </>
  );
};

export default Agreements;
